import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback Component
 * Handles the OAuth callback from Emergent Auth.
 * Exchanges session_id for app session and redirects to home.
 * 
 * NOTE: This component uses window.location for navigation because it may be
 * rendered outside of React Router context when handling OAuth callbacks.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = ({ onAuthSuccess }) => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id found in URL');
        // Use window.location for navigation (works outside Router context)
        window.location.href = '/';
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        // Exchange session_id for our app's session
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.success && response.data.user) {
          // Store user in localStorage for quick access
          localStorage.setItem('my_doctor_user', JSON.stringify(response.data.user));
          localStorage.setItem('user_email', response.data.user.email);
          localStorage.setItem('user_name', response.data.user.name);
          
          // Notify parent component
          if (onAuthSuccess) {
            onAuthSuccess(response.data.user);
          }

          // Redirect to home (clears hash from URL)
          window.location.href = '/';
        } else {
          throw new Error('Invalid response from auth');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        // Clear any stale data
        localStorage.removeItem('my_doctor_user');
        window.location.href = '/';
      }
    };

    processAuth();
  }, [onAuthSuccess]);

  // Show loading while processing
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center" dir="rtl">
      <div className="text-center">
        <Loader2 className="w-16 h-16 text-teal-400 animate-spin mx-auto mb-4" />
        <p className="text-xl font-bold text-white">מתחבר...</p>
        <p className="text-slate-400 mt-2">אנא המתן</p>
      </div>
    </div>
  );
};

/**
 * Login Button Component
 * Initiates Google OAuth via Emergent Auth
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const LoginButton = ({ className, children }) => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <button
      onClick={handleLogin}
      className={className || "bg-white text-slate-900 px-6 py-3 rounded-xl font-bold flex items-center gap-3 hover:bg-slate-100 transition-colors"}
      data-testid="google-login-button"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      {children || 'התחבר עם Google'}
    </button>
  );
};

/**
 * Logout Button Component
 */
const LogoutButton = ({ onLogout, className, children }) => {
  const handleLogout = async () => {
    try {
      const response = await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Clear local storage
        localStorage.removeItem('my_doctor_user');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        localStorage.removeItem('my_doctor_subscription');
        localStorage.removeItem('chaser_user_session');
        
        if (onLogout) {
          onLogout();
        }
        
        // Refresh page to reset state
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Clear anyway on error
      localStorage.clear();
      window.location.href = '/';
    }
  };

  return (
    <button
      onClick={handleLogout}
      className={className || "text-slate-500 hover:text-slate-700 text-sm font-medium"}
      data-testid="logout-button"
    >
      {children || 'התנתק'}
    </button>
  );
};

/**
 * useAuth Hook
 * Check authentication status and get user data
 */
const useAuth = () => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        // Check localStorage first for quick UI
        const savedUser = localStorage.getItem('my_doctor_user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
          setIsAuthenticated(true);
        }

        // Verify with server
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });

        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
          localStorage.setItem('my_doctor_user', JSON.stringify(response.data));
        }
      } catch (error) {
        // Not authenticated
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('my_doctor_user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  return { user, loading, isAuthenticated, setUser };
};

export { AuthCallback, LoginButton, LogoutButton, useAuth };
